import { useEffect, useMemo, useState } from "react";

/** Util petites helpers **/
const slugify = (str = "") =>
  String(str)
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)+/g, "");

const deepClone = (obj) => JSON.parse(JSON.stringify(obj));

const EMPTY_TEMPLATE = {
  idTemplate: null,
  nameTemplate: "",
  descriptionTemplate: "",
  steps: [],
};

const EMPTY_STEP = (index) => ({
  idStep: null,
  slugStep: `step-${index + 1}`,
  title: "",
  descriptionStep: "",
  stepOrder: index, // 0-based en interne, on mappe à la sauvegarde si besoin
  questions: [],
});

const EMPTY_QUESTION = {
  idQuestion: null,
  questionType: "text", // text, textarea, number, date, time, select, radio, checkbox, file
  label: "",
  options: [], // pour select/radio/checkbox
  isRequired: false,
};

const QUESTION_TYPES_WITH_OPTIONS = new Set(["select", "radio", "checkbox"]);

export default function FormTemplateEditor({ templateId = null }) {
  const apiUrl = process.env.REACT_APP_API_URL;

  const [template, setTemplate] = useState(EMPTY_TEMPLATE);
  const [isSaving, setIsSaving] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [saveError, setSaveError] = useState("");
  const [saveOk, setSaveOk] = useState("");

  /** Charger un template existant si templateId est fourni **/
  useEffect(() => {
    let ignore = false;
    const load = async () => {
      if (!templateId) return;
      setLoadError("");
      try {
        const res = await fetch(`${apiUrl}/api/form-templates/${templateId}`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (!ignore) {
          // On normalise un peu ce qui arrive (stepOrder en index 0-based local)
          const normalized = deepClone(data);
          normalized.steps = (normalized.steps || [])
            .sort((a, b) => (a.stepOrder ?? 0) - (b.stepOrder ?? 0))
            .map((s, i) => ({
              ...s,
              stepOrder: i,
              questions: (s.questions || []).map((q) => ({
                ...q,
                options: Array.isArray(q.options) ? q.options : [],
                isRequired: !!q.isRequired,
              })),
            }));
          setTemplate(normalized);
        }
      } catch (e) {
        if (!ignore) setLoadError("Impossible de charger le template.");
      }
    };
    load();
    return () => { ignore = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateId, apiUrl]);

  /** Handlers Template **/
  const setField = (key, value) =>
    setTemplate((t) => ({ ...t, [key]: value }));

  const addStep = () =>
    setTemplate((t) => {
      const next = deepClone(t);
      next.steps.push(EMPTY_STEP(next.steps.length));
      return next;
    });

  const updateStep = (idx, patch) =>
    setTemplate((t) => {
      const next = deepClone(t);
      next.steps[idx] = { ...next.steps[idx], ...patch };
      // slug auto si titre change et slug vide ou auto
      if ("title" in patch) {
        const s = next.steps[idx];
        const wasAuto = !s.slugStep || s.slugStep.startsWith("step-");
        if (wasAuto || !s.slugStep) {
          s.slugStep = slugify(patch.title || `step-${idx + 1}`);
        }
      }
      return next;
    });

  const removeStep = (idx) =>
    setTemplate((t) => {
      const next = deepClone(t);
      next.steps.splice(idx, 1);
      // Réindexer l'ordre
      next.steps = next.steps.map((s, i) => ({ ...s, stepOrder: i }));
      return next;
    });

  const moveStep = (from, direction) =>
    setTemplate((t) => {
      const next = deepClone(t);
      const to = direction === "up" ? from - 1 : from + 1;
      if (to < 0 || to >= next.steps.length) return t;
      const temp = next.steps[from];
      next.steps[from] = next.steps[to];
      next.steps[to] = temp;
      next.steps = next.steps.map((s, i) => ({ ...s, stepOrder: i }));
      return next;
    });

  /** Handlers Question **/
  const addQuestion = (stepIndex) =>
    setTemplate((t) => {
      const next = deepClone(t);
      next.steps[stepIndex].questions.push({ ...EMPTY_QUESTION });
      return next;
    });

  const updateQuestion = (stepIndex, qIndex, patch) =>
    setTemplate((t) => {
      const next = deepClone(t);
      const q = next.steps[stepIndex].questions[qIndex];
      const merged = { ...q, ...patch };

      // Si le type change et ne prend pas d'options → on vide options
      if (
        "questionType" in patch &&
        !QUESTION_TYPES_WITH_OPTIONS.has(patch.questionType)
      ) {
        merged.options = [];
      }
      next.steps[stepIndex].questions[qIndex] = merged;
      return next;
    });

  const removeQuestion = (stepIndex, qIndex) =>
    setTemplate((t) => {
      const next = deepClone(t);
      next.steps[stepIndex].questions.splice(qIndex, 1);
      return next;
    });

  const moveQuestion = (stepIndex, from, direction) =>
    setTemplate((t) => {
      const next = deepClone(t);
      const to = direction === "up" ? from - 1 : from + 1;
      if (to < 0 || to >= next.steps[stepIndex].questions.length) return t;
      const list = next.steps[stepIndex].questions;
      const temp = list[from];
      list[from] = list[to];
      list[to] = temp;
      return next;
    });

  /** Options côté UI: saisie sous forme "opt1, opt2, opt3" **/
  const optionsString = (opts) => (opts || []).join(", ");
  const parseOptions = (str) =>
    str
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);

  /** Validation rapide **/
  const validationErrors = useMemo(() => {
    const errs = [];
    if (!template.nameTemplate?.trim()) errs.push("Le nom du template est requis.");
    template.steps.forEach((s, i) => {
      if (!s.title?.trim()) errs.push(`Le titre du step #${i + 1} est requis.`);
      s.questions.forEach((q, j) => {
        if (!q.label?.trim()) errs.push(`La question #${j + 1} du step #${i + 1} doit avoir un libellé.`);
        if (QUESTION_TYPES_WITH_OPTIONS.has(q.questionType) && (!q.options || q.options.length === 0)) {
          errs.push(`La question #${j + 1} du step #${i + 1} doit avoir des options.`);
        }
      });
    });
    return errs;
  }, [template]);

  /** Payload prêt pour l’API **/
  const payload = useMemo(() => {
    const t = deepClone(template);
    t.steps = (t.steps || []).map((s, i) => ({
      ...s,
      stepOrder: i + 1, // si tu préfères 1-based côté DB, sinon laisse i
      slugStep: s.slugStep || slugify(s.title || `step-${i + 1}`),
      questions: (s.questions || []).map((q) => ({
        ...q,
        options: Array.isArray(q.options) ? q.options : [],
        isRequired: !!q.isRequired,
      })),
    }));
    return t;
  }, [template]);

  /** Save **/
  const onSave = async () => {
    setSaveOk("");
    setSaveError("");

    if (validationErrors.length) {
      setSaveError("Veuillez corriger les erreurs avant d’enregistrer.");
      return;
    }

    setIsSaving(true);
    try {
      const method = template.idTemplate ? "PUT" : "POST";
      const url = template.idTemplate
        ? `${apiUrl}/api/form-templates/${template.idTemplate}`
        : `${apiUrl}/api/form-templates`;

      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data?.error || `Erreur API (${res.status})`);
      }

      // Si l’API renvoie l’objet, on peut rafraîchir le state
      if (data?.template) {
        const normalized = deepClone(data.template);
        normalized.steps = (normalized.steps || [])
          .sort((a, b) => (a.stepOrder ?? 0) - (b.stepOrder ?? 0))
          .map((s, i) => ({ ...s, stepOrder: i }));
        setTemplate(normalized);
      } else if (data?.id) {
        setTemplate((t) => ({ ...t, idTemplate: data.id }));
      }

      setSaveOk("Template enregistré avec succès ✅");
    } catch (e) {
      setSaveError(e.message || "Échec de l’enregistrement.");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div style={{ maxWidth: 1100, margin: "20px auto", padding: 16 }}>
      <h1 style={{ marginBottom: 12 }}>
        {template.idTemplate ? "Modifier un template" : "Créer un template"}
      </h1>

      {loadError && (
        <div style={{ background: "#ffe4e6", padding: 12, borderRadius: 8, marginBottom: 12 }}>
          {loadError}
        </div>
      )}

      <section
        style={{
          display: "grid",
          gridTemplateColumns: "1fr",
          gap: 12,
          background: "#fff",
          padding: 16,
          borderRadius: 12,
          boxShadow: "0 2px 8px rgba(0,0,0,.06)",
          marginBottom: 16,
        }}
      >
        <label style={{ display: "grid", gap: 6 }}>
          <span>Nom du template *</span>
          <input
            value={template.nameTemplate}
            onChange={(e) => setField("nameTemplate", e.target.value)}
            placeholder="Ex: Bricolage standard"
          />
        </label>

        <label style={{ display: "grid", gap: 6 }}>
          <span>Description</span>
          <textarea
            rows={3}
            value={template.descriptionTemplate}
            onChange={(e) => setField("descriptionTemplate", e.target.value)}
            placeholder="Description courte…"
          />
        </label>
      </section>

      {/* Steps */}
      <section style={{ display: "grid", gap: 12 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h2 style={{ margin: 0 }}>Étapes ({template.steps.length})</h2>
          <button onClick={addStep}>+ Ajouter une étape</button>
        </div>

        {template.steps.map((step, sIdx) => (
          <div
            key={`step-${sIdx}-${step.idStep ?? "new"}`}
            style={{
              background: "#fff",
              padding: 16,
              borderRadius: 12,
              boxShadow: "0 2px 8px rgba(0,0,0,.06)",
            }}
          >
            <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 12 }}>
              <strong style={{ fontSize: 16 }}>Étape #{sIdx + 1}</strong>
              <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
                <button onClick={() => moveStep(sIdx, "up")} disabled={sIdx === 0}>
                  ↑
                </button>
                <button
                  onClick={() => moveStep(sIdx, "down")}
                  disabled={sIdx === template.steps.length - 1}
                >
                  ↓
                </button>
                <button onClick={() => removeStep(sIdx)} style={{ color: "#b91c1c" }}>
                  Supprimer
                </button>
              </div>
            </div>

            <div style={{ display: "grid", gap: 8 }}>
              <label style={{ display: "grid", gap: 4 }}>
                <span>Titre *</span>
                <input
                  value={step.title}
                  onChange={(e) => updateStep(sIdx, { title: e.target.value })}
                  placeholder="Ex: Informations générales"
                />
              </label>

              <label style={{ display: "grid", gap: 4 }}>
                <span>Slug (auto si vide)</span>
                <input
                  value={step.slugStep}
                  onChange={(e) => updateStep(sIdx, { slugStep: e.target.value })}
                  placeholder="ex: informations-generales"
                />
              </label>

              <label style={{ display: "grid", gap: 4 }}>
                <span>Description</span>
                <textarea
                  rows={2}
                  value={step.descriptionStep}
                  onChange={(e) => updateStep(sIdx, { descriptionStep: e.target.value })}
                />
              </label>
            </div>

            {/* Questions */}
            <div style={{ marginTop: 12, borderTop: "1px solid #eee", paddingTop: 12 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <h3 style={{ margin: 0 }}>Questions ({step.questions.length})</h3>
                <button onClick={() => addQuestion(sIdx)}>+ Ajouter une question</button>
              </div>

              {step.questions.map((q, qIdx) => {
                const withOptions = QUESTION_TYPES_WITH_OPTIONS.has(q.questionType);
                return (
                  <div
                    key={`q-${sIdx}-${qIdx}-${q.idQuestion ?? "new"}`}
                    style={{
                      marginTop: 10,
                      padding: 12,
                      borderRadius: 10,
                      border: "1px solid #e5e7eb",
                      background: "#fafafa",
                    }}
                  >
                    <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                      <strong>Q{qIdx + 1}</strong>
                      <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
                        <button onClick={() => moveQuestion(sIdx, qIdx, "up")} disabled={qIdx === 0}>
                          ↑
                        </button>
                        <button
                          onClick={() => moveQuestion(sIdx, qIdx, "down")}
                          disabled={qIdx === step.questions.length - 1}
                        >
                          ↓
                        </button>
                        <button
                          onClick={() => removeQuestion(sIdx, qIdx)}
                          style={{ color: "#b91c1c" }}
                        >
                          Supprimer
                        </button>
                      </div>
                    </div>

                    <div
                      style={{
                        marginTop: 8,
                        display: "grid",
                        gridTemplateColumns: "1fr 180px 120px",
                        gap: 8,
                        alignItems: "center",
                      }}
                    >
                      <input
                        placeholder="Libellé de la question *"
                        value={q.label}
                        onChange={(e) => updateQuestion(sIdx, qIdx, { label: e.target.value })}
                      />

                      <select
                        value={q.questionType}
                        onChange={(e) =>
                          updateQuestion(sIdx, qIdx, { questionType: e.target.value })
                        }
                      >
                        <option value="text">Texte</option>
                        <option value="textarea">Zone de texte</option>
                        <option value="number">Nombre</option>
                        <option value="date">Date</option>
                        <option value="time">Heure</option>
                        <option value="select">Select</option>
                        <option value="radio">Radio</option>
                        <option value="checkbox">Checkbox</option>
                        <option value="file">Fichier</option>
                      </select>

                      <label style={{ display: "flex", gap: 6, alignItems: "center" }}>
                        <input
                          type="checkbox"
                          checked={!!q.isRequired}
                          onChange={(e) =>
                            updateQuestion(sIdx, qIdx, { isRequired: e.target.checked })
                          }
                        />
                        Requis
                      </label>
                    </div>

                    {withOptions && (
                      <div style={{ marginTop: 8 }}>
                        <small>Options (séparées par des virgules)</small>
                        <input
                          style={{ width: "100%", marginTop: 4 }}
                          placeholder="ex: Option A, Option B, Option C"
                          value={optionsString(q.options)}
                          onChange={(e) =>
                            updateQuestion(sIdx, qIdx, { options: parseOptions(e.target.value) })
                          }
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </section>

      {/* Erreurs validation */}
      {validationErrors.length > 0 && (
        <div
          style={{
            marginTop: 16,
            padding: 12,
            borderRadius: 8,
            background: "#fff7ed",
            border: "1px solid #fdba74",
          }}
        >
          <strong>À corriger :</strong>
          <ul style={{ margin: "8px 0 0 16px" }}>
            {validationErrors.map((e, i) => (
              <li key={i}>{e}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Actions */}
      <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 16 }}>
        <button onClick={onSave} disabled={isSaving}>
          {isSaving ? "Enregistrement…" : template.idTemplate ? "Mettre à jour" : "Créer"}
        </button>
        {saveOk && <span style={{ color: "#16a34a" }}>{saveOk}</span>}
        {saveError && <span style={{ color: "#b91c1c" }}>{saveError}</span>}
      </div>

      {/* Aperçu JSON */}
      <details style={{ marginTop: 16 }}>
        <summary>Aperçu JSON envoyé à l’API</summary>
        <pre
          style={{
            marginTop: 8,
            background: "#0b1020",
            color: "#d1e7ff",
            padding: 12,
            borderRadius: 8,
            overflow: "auto",
            maxHeight: 360,
          }}
        >
{JSON.stringify(payload, null, 2)}
        </pre>
      </details>
    </div>
  );
}
